import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Text, Content, FlexboxGrid, Form, Col, Row, Stack } from 'rsuite'
import {
    updatePermitApplicationData,
} from '../../store/slices/apply/applySlice'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import { selectConfigExpressApplyDocuments } from '../../store/slices/config/configSelectors'
import DocumentUpload from '../../components/form/DocumentUpload'
import Notice from '../../components/user-feedback/Notice'
import {
    constructMarkup,
    isNullUndefinedOrEmptyObject,
    objectHasProperty,
} from '../../common/utils'
import { setErrorMessage } from '../../store/slices/auth/authSlice'
import FormFieldErrorIcon from '../../components/form/FormFieldErrorIcon'
import { FLOWS_ENUM } from '../../common/enums'

const ApplyDocuments = () => {
    const dispatch = useDispatch()
    const [hasError, setHasError] = useState(false)
    const [pageErrorMessage, setPageErrorMessage] = useState('')
    const [uploadValidationErrors, setUploadValidationErrors] = useState([])
    const [fileLists, setFileLists] = useState({})
    const [documentError, setDocumentError] = useState("");

    const selectedDocuments = useSelector(selectConfigExpressApplyDocuments)

    useEffect(() => {
        // Function dependencies
        const setFileListConfiguration = () => {
            let tempFileLists = { ...fileLists }
            selectedDocuments?.forEach((document) => {
                if (!objectHasProperty(document.file_key, fileLists)) {
                    tempFileLists[document.file_key] = {
                        required: document.required,
                        attachedFiles: [],
                    }
                }
            })
            setFileLists(tempFileLists)
        }

        // Effect logic
        setFileListConfiguration()
    }, [selectedDocuments])



    const onFileListChanged = (documentKey, files) => {
        clearUploadValidationError(documentKey)
        setHasError(false)
        setErrorMessage('')
        fileLists[documentKey].attachedFiles = files
    }

    const clearUploadValidationError = (documentKey) => {
        let errors = { ...uploadValidationErrors }
        delete errors[documentKey]
        setUploadValidationErrors(errors)
    }
    const uploadDocuments = async () => {
        let uploadRequirementResults = validateFileUploadRequirements()
        if (uploadRequirementResults.success === true) {
            dispatchDocumentsToStore()
        } else {
            let validationErrors = {}
            uploadRequirementResults.errors.forEach((err) => {
                Object.keys(err).forEach((k) => {
                    validationErrors[k] = err[k]
                })
            })
            setUploadValidationErrors(validationErrors)
            setHasError(true)
            setPageErrorMessage(
                'One or more required documents have not been provided.'
            )
        }
        return uploadRequirementResults
    }

    const validateFileUploadRequirements = () => {
        let result = {
            success: true,
            errors: [],
        }
        Object.keys(fileLists).forEach((k) => {
            if (fileLists[k].required === true) {
                if (
                    !fileLists[k].attachedFiles ||
                    !Array.isArray(fileLists[k].attachedFiles) ||
                    fileLists[k]?.attachedFiles?.length === 0
                ) {
                    result.success = false
                    result.errors.push({ [k]: true })
                }
            }
        })
        return result
    }

    const dispatchDocumentsToStore = () => {
        dispatch(
            updatePermitApplicationData({
                documents: fileLists,
            })
        )
    }

    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Document Upload"
                        onNext={async () => {
                            let docsUploadResult = await uploadDocuments()
                            return docsUploadResult.success;
                        }}
                    >
                        <Form fluid>
                            <FlexboxGrid className="mt-12 ml-0" xs={24} lg={12}>
                                <FlexboxGrid.Item colspan={24}>
                                    <div role="grid">
                                        <Row>
                                            <Col>
                                                <Text>
                                                    Your application requires the
                                                    below documents to be uploaded.
                                                </Text>
                                                <Text className="paragraph">
                                                    Supported formats: .jpg, .jpeg,
                                                    .png, .pdf, .heic; Minimum file
                                                    size: 15 KB; Maximum file size:
                                                    4 MB; Maximum number of files
                                                    per document type: 20.
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                    colspan={24}
                                    className="mt-24"
                                >
                                    <div role="grid">
                                        <Row style={{ minHeight: '60px' }}>
                                            <Col xs={24}>
                                                {!isNullUndefinedOrEmptyObject(
                                                    fileLists
                                                ) &&
                                                    selectedDocuments?.map(
                                                        (document) => {
                                                            //const ref = AddRef(idx);
                                                            const fileList = fileLists[document.file_key].attachedFiles
                                                            return (
                                                                <FlexboxGrid
                                                                    key={`document_upload_${document.file_key}`}
                                                                    justify="start"
                                                                    align="middle"
                                                                    className="mb-24"
                                                                >
                                                                    <FlexboxGrid.Item
                                                                        colspan={24}
                                                                    >
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={
                                                                                4
                                                                            }
                                                                        >
                                                                            <Text className="text-bold">
                                                                                {
                                                                                    constructMarkup(document.title)
                                                                                }
                                                                            </Text>
                                                                            {uploadValidationErrors[document.file_key] && (
                                                                                <FormFieldErrorIcon errorMessage="These documents are required." />
                                                                            )}
                                                                        </Stack>
                                                                    </FlexboxGrid.Item>
                                                                    <FlexboxGrid.Item
                                                                        colspan={24}
                                                                    >
                                                                        <Text>
                                                                            {
                                                                                constructMarkup(document.description)
                                                                            }
                                                                        </Text>
                                                                    </FlexboxGrid.Item>
                                                                    <FlexboxGrid.Item
                                                                        className="mt-8"
                                                                        colspan={24}
                                                                    >
                                                                        <DocumentUpload
                                                                            ariaLabel={document.file_key}
                                                                            allowMultiple={true}
                                                                            fileList={fileList?.attachedFiles}
                                                                            onChange={(documents) => onFileListChanged(document.file_key,documents)}
                                                                            validExtensions={
                                                                                [
                                                                                    '.jpg',
                                                                                    '.jpeg',
                                                                                    '.png',
                                                                                    '.pdf',
                                                                                    '.heic'
                                                                                ]
                                                                            }
                                                                            maxDocuments={20}
                                                                            onError={(msg) => {
                                                                                setHasError(msg && msg.length > 0);
                                                                                setPageErrorMessage(msg)
                                                                            }}
                                                                        />
                                                                    </FlexboxGrid.Item>
                                                                </FlexboxGrid>
                                                            )
                                                        }
                                                    )}
                                            </Col>
                                        </Row>
                                    </div>
                                </FlexboxGrid.Item>
                                {hasError && (
                                    <FlexboxGrid.Item
                                        colspan={24}
                                        className="mt-24"
                                    >
                                        <div role="grid">
                                            <Row style={{ minHeight: '60px' }}>
                                                <Col xs={24}>
                                                    <Notice
                                                        message={
                                                            pageErrorMessage?.length >
                                                            0
                                                                ? pageErrorMessage
                                                                : 'An error occurred while uploading documents.'
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    </FlexboxGrid.Item>
                                )}
                            </FlexboxGrid>
                        </Form>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default ApplyDocuments
